import { jwtDecode } from 'tio-common/utils/jwt-decode';

/** The top-level route that receives a landing-preview grant from tio-admin (RFC 128). */
export const PREVIEW_PATH = '/preview';

export type PreviewSurface = 'partner' | 'employee';
export type PreviewExperience = 'current' | 'beta';

/** The `preview` claim of a landing-preview grant as the client reads it; the grant's scope is the API's to enforce. */
export interface PreviewClaim {
  /** User id of the TIO admin the grant was minted for. */
  by: number;
  surface: PreviewSurface;
  company_id: number;
  read_only: true;
}

/** The postMessage protocol between the admin host and the framed preview: `{ type }`, and `token` on `grant`. */
export const PREVIEW_MESSAGE = {
  /** Preview → host: the route is listening and wants its grant. */
  ready: 'tio-preview-ready',
  /** Host → preview: the grant minted by `POST /api/v2/landing-previews`. */
  grant: 'tio-preview-grant',
  /** Preview → host: authenticated and showing the surface's landing route. */
  started: 'tio-preview-started',
  /** Preview → host: the grant expired or was refused; nothing shown is live any more. */
  expired: 'tio-preview-expired',
} as const;

export interface PreviewGrantMessage {
  type: typeof PREVIEW_MESSAGE.grant;
  token: string;
}

/** The window that framed (or opened) the preview and will hand over its grant. */
export interface PreviewHost {
  postMessage(message: unknown, targetOrigin: string): void;
}

const SURFACES: readonly string[] = ['partner', 'employee'];
const EXPERIENCES: readonly string[] = ['current', 'beta'];

/** sessionStorage key marking this browsing context as a preview frame, so a reload in it never boots a persistent session. */
const PREVIEW_BOOT_KEY = 'tio-preview-boot';

/** What a reload inside the frame needs to restart the hand-off. */
export interface PreviewBootMark {
  surface: PreviewSurface;
  experience: PreviewExperience;
}

/** Whether this page load is the preview hand-off; reads `location` because callers run before the router has a route. */
export function isPreviewPath(pathname?: string): boolean {
  const path = pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  return path.replace(/\/+$/, '') === PREVIEW_PATH;
}

/**
 * Marks this browsing context as a preview frame for the rest of its life.
 *
 * @returns whether the mark reads back as written; false means a reload here could not be kept in-memory, so no grant may be seated
 */
export function writePreviewBootMark(mark: PreviewBootMark): boolean {
  try {
    sessionStorage.setItem(PREVIEW_BOOT_KEY, JSON.stringify(mark));
  } catch {
    return false;
  }
  const stored = readPreviewBootMark();
  return stored?.surface === mark.surface && stored.experience === mark.experience;
}

/** The mark an earlier hand-off left in this browsing context, or null (including a malformed one). */
export function readPreviewBootMark(): PreviewBootMark | null {
  try {
    const raw = sessionStorage.getItem(PREVIEW_BOOT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PreviewBootMark> | null;
    const surface = parseSurface(parsed?.surface);
    return surface ? { surface, experience: parseExperience(parsed?.experience) } : null;
  } catch {
    return null;
  }
}

export function clearPreviewBootMark(): void {
  try {
    sessionStorage.removeItem(PREVIEW_BOOT_KEY);
  } catch {
    return;
  }
}

/** Whether this browsing context is a preview: on the hand-off route, or marked by a hand-off before a reload. */
export function isPreviewBoot(pathname?: string): boolean {
  return isPreviewPath(pathname) || readPreviewBootMark() !== null;
}

/** @returns the surface named by a query param, or null for anything else */
export function parseSurface(value: unknown): PreviewSurface | null {
  return typeof value === 'string' && SURFACES.includes(value) ? (value as PreviewSurface) : null;
}

/** @returns the experience named by a query param; anything unrecognised is the current one */
export function parseExperience(value: unknown): PreviewExperience {
  return typeof value === 'string' && EXPERIENCES.includes(value)
    ? (value as PreviewExperience)
    : 'current';
}

/** The claim carried by a token, or null for anything but a well-formed read-only grant (undecodable tokens included). */
export function readPreviewClaim(token: string): PreviewClaim | null {
  let claim: Partial<PreviewClaim> | undefined;
  try {
    claim = jwtDecode<{ preview?: Partial<PreviewClaim> }>(token).preview;
  } catch {
    return null;
  }
  if (
    !claim ||
    claim.read_only !== true ||
    typeof claim.by !== 'number' ||
    typeof claim.company_id !== 'number' ||
    !parseSurface(claim.surface)
  ) {
    return null;
  }
  return {
    by: claim.by,
    surface: claim.surface as PreviewSurface,
    company_id: claim.company_id,
    read_only: true,
  };
}

/** Whether a token carries a `preview` claim at all, well-formed or not. */
export function hasPreviewClaim(token: string): boolean {
  try {
    return 'preview' in jwtDecode<Record<string, unknown>>(token);
  } catch {
    return false;
  }
}

export function isPreviewGrantMessage(data: unknown): data is PreviewGrantMessage {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const message = data as Partial<PreviewGrantMessage>;
  return message.type === PREVIEW_MESSAGE.grant && typeof message.token === 'string';
}

/** The route a surface's preview lands on; both live under `authenticated`. */
export function landingRouteFor(surface: PreviewSurface) {
  return surface === 'partner' ? 'authenticated.admin' : 'authenticated.dashboard';
}

/** The admin window that can hand over a grant: the parent when framed, else the opener, else null. */
export function previewHost(): PreviewHost | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (window.parent && window.parent !== window) {
    return window.parent;
  }
  return window.opener ?? null;
}
